<div class="flex flex-col gap-4" *transloco="let t">
  <!-- Current Pack Header -->

  <section
    class="flex items-center justify-between rounded-lg border border-greyscale-200 bg-primary-50 p-4"
  >
    <div class="flex gap-4">
      <div class="flex items-center gap-2">
        <i class="pi pi-box text-primary-500"></i>
        <h3 class="text-sm font-semibold text-greyscale-700">
          {{ t("subscription.currentPack") }}
        </h3>
      </div>

      <div class="flex items-center gap-2">
        <span class="text-md text-greyscale-700">{{ pack?.name }}</span>
      </div>
    </div>
    @if (displayCTA) {
      <p-button
        [label]="t('subscription.upgradePack')"
        icon="pi pi-arrow-right"
        iconPos="right"
        [text]="true"
        (onClick)="openUpgradeModal()"
      />
    }
  </section>

  <!-- Trips and Users Cards -->
  <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
    <!-- Trips Card -->
    <section
      class="flex flex-col gap-4 rounded-lg border border-greyscale-200 bg-primary-50 p-4"
    >
      <div class="flex items-center gap-2">
        <app-greentripper-pin-icon
          styleClass="w-5 h-5 text-primary-600 ml-[2px]"
        />
        <h3 class="text-sm font-semibold text-greyscale-700">
          {{ t("subscription.trips") }}
        </h3>
      </div>

      <div class="flex items-center justify-between">
        <span class="text-greyscale-600">
          {{ t("subscription.tripsIncluded") }}:
        </span>
        <span class="font-semibold text-greyscale-800">
          {{ pack?.tripLimit }}
        </span>
      </div>
      <div class="flex flex-col gap-2">
        <p-progressBar
          [value]="progressPercentageTrip()"
          [style]="{ height: '8px' }"
          [showValue]="false"
          [dt]="dtProgressbar"
        />
        <div class="self-end text-sm text-greyscale-600">
          {{ t("subscription.tripsUsed", { used: usedTrips ?? 0 }) }}
        </div>
      </div>
      @if (displayCTA) {
        <p-button
          [label]="t('subscription.addTrips')"
          icon="pi pi-plus"
          [outlined]="true"
          styleClass="w-full"
          (onClick)="openAddTripModal()"
        />
      }
    </section>

    <!-- Users Card -->
    <section
      class="flex flex-col gap-4 rounded-lg border border-greyscale-200 bg-primary-50 p-4"
    >
      <div class="flex items-center gap-2">
        <i class="pi pi-user text-primary-500"></i>
        <h3 class="text-sm font-semibold text-greyscale-700">
          {{ t("subscription.users") }}
        </h3>
      </div>
      <div class="flex items-center justify-between">
        <span class="text-greyscale-600">
          {{ t("subscription.includedUsers") }}:
        </span>
        <span class="font-semibold text-greyscale-800">
          {{ pack?.userLimit }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <p-progressBar
          [value]="progressPercentageUsers()"
          [style]="{ height: '8px' }"
          [showValue]="false"
          [dt]="dtProgressbar"
        />
        <div class="self-end text-sm text-greyscale-600">
          {{ t("subscription.userCreated", { created: usedUsers ?? 0 }) }}
        </div>
      </div>

      @if (displayCTA) {
        <p-button
          [label]="t('subscription.addUser')"
          icon="pi pi-plus"
          [outlined]="true"
          styleClass="w-full"
          (onClick)="openAddUserModal()"
        />
      }
    </section>
  </div>

  <!-- Features -->
  <div
    class="flex flex-col gap-4 rounded-lg border border-greyscale-200 bg-primary-50 p-4"
  >
    <div class="flex items-center gap-2">
      <i class="pi pi-star text-primary-500"></i>
      <h3 class="text-sm font-semibold text-greyscale-700">
        {{ t("subscription.features") }}
      </h3>
    </div>

    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <!-- Included in all packs -->
      <div class="flex items-center gap-3">
        <i class="pi pi-check text-lg text-green-600"></i>
        <span class="text-greyscale-600">
          {{ t("subscription.advancedEmissionCalculation") }}
        </span>
      </div>

      <div class="flex items-center gap-3">
        <i class="pi pi-check text-lg text-green-600"></i>
        <span class="text-greyscale-600">
          {{ t("subscription.aiBrochureAnalysis") }}
        </span>
      </div>

      <div class="flex items-center gap-3">
        <i class="pi pi-check text-lg text-green-600"></i>
        <span class="text-greyscale-600">
          {{ t("subscription.updatedEmissionFactors") }}
        </span>
      </div>

      <!-- Pack-specific features -->
      <div class="flex items-center gap-3">
        <i
          class="pi text-lg"
          [ngClass]="
            pack?.exportPDF
              ? 'pi-check text-green-600'
              : 'pi-times text-red-500'
          "
        ></i>
        <span class="text-greyscale-600">
          {{ t("subscription.exportPdf") }}
        </span>
      </div>

      <div class="flex items-center gap-3">
        <i
          class="pi text-lg"
          [ngClass]="
            pack?.exportExcel
              ? 'pi-check text-green-600'
              : 'pi-times text-red-500'
          "
        ></i>
        <span class="text-greyscale-600">
          {{ t("subscription.exportExcel") }}
        </span>
      </div>

      <div class="flex items-center gap-3">
        <i
          class="pi text-lg"
          [ngClass]="
            pack?.clientFilter
              ? 'pi-check text-green-600'
              : 'pi-times text-red-500'
          "
        ></i>
        <span class="text-greyscale-600">
          {{ t("subscription.clientFilter") }}
        </span>
      </div>

      <div class="flex items-center gap-3">
        <i
          class="pi text-lg"
          [ngClass]="
            pack?.bulkUpload
              ? 'pi-check text-green-600'
              : 'pi-times text-red-500'
          "
        ></i>
        <span class="text-greyscale-600">
          {{ t("subscription.bulkUpload") }}
        </span>
      </div>

      <div class="flex items-center gap-3">
        <i
          class="pi text-lg"
          [ngClass]="
            pack?.comparisonGraph
              ? 'pi-check text-green-600'
              : 'pi-times text-red-500'
          "
        ></i>
        <span class="text-greyscale-600">
          {{ t("subscription.comparisonGraph") }}
        </span>
      </div>

      @if (pack?.useApi) {
        <div class="flex items-center gap-3">
          <i class="pi pi-check text-lg text-green-600"></i>
          <span class="text-greyscale-600">
            {{ t("subscription.useApi") }}
          </span>
        </div>
      }

      @if (pack?.useGoogleApi) {
        <div class="flex items-center gap-3">
          <i class="pi pi-check text-lg text-green-600"></i>
          <span class="text-greyscale-600">
            {{ t("subscription.useGoogleApi") }}
          </span>
        </div>
      }
    </div>

    @if (hasLockedFeatures && displayCTA) {
      <div class="mt-5 flex justify-center border-t border-greyscale-100 pt-5">
        <p-button
          [label]="t('subscription.upgradeToUnlock')"
          icon="pi pi-arrow-right"
          iconPos="right"
          (onClick)="openUpgradeModal()"
        />
      </div>
    }
  </div>
</div>
